import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import StatusChip from 'components/@extended/StatusChip';
import MatterIndicators from 'components/matters/MatterIndicators';
import PartyName from 'components/party/PartyName';
import { RecordChip } from 'components/record-chip/RecordChip';
import { Button } from 'components/ui/button';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import TimeSinceDate from 'components/util/TimeSinceDate';
import {
    draftNotificationKind,
    draftNotificationValues,
    draftTile,
} from 'layout/MainLayout/Header/HeaderContent/actions/Notifications/draftNotification';
import FormattedNotifcationText from 'layout/MainLayout/Header/HeaderContent/actions/Notifications/FormattedNotificationText';
import { useDispatch } from 'store';
import { closeTemporaryDrawer } from 'store/reducers/temporaryDrawer';
import { ClassifierData } from 'types/classifiers';
import { AddressedTaskListItem, getPriorityStatusChipType, getTaskUrl } from 'types/tasks';
import { cn } from 'utils/cn';
import { getClassifierText, getIntl, t } from 'utils/getTranslations';

import { FeedEntry, InboxCategory } from './inboxFeed';

const ROW_ACTION_CLASS = 'text-muted-foreground hover:text-foreground text-caption h-auto p-0 hover:no-underline';

/** The tinted square that names a row's category by its glyph; a draft's names its record instead. */
export function CategoryTile({ category, className }: { category: InboxCategory; className?: string }) {
    const Icon = category.icon;
    return (
        <span
            className={cn(
                'flex size-[34px] flex-none items-center justify-center rounded-[9px]',
                category.tileClassName,
                className
            )}
        >
            <Icon className="size-[18px]" />
        </span>
    );
}

/** The frame every feed row shares: the unread dot, the tile, the title line, the body and the footer. */
function FeedRow({
    category,
    isUnread,
    title,
    time,
    body,
    footer,
}: {
    category: InboxCategory;
    isUnread: boolean;
    title: ReactNode;
    time?: ReactNode;
    body: ReactNode;
    footer: ReactNode;
}) {
    return (
        <li className="hover:bg-row-stripe relative flex gap-3.5 py-3 ps-9 pe-6">
            <span
                aria-hidden
                className={cn(
                    'bg-error absolute start-[18px] top-[26px] size-2 rounded-full transition-opacity duration-150',
                    !isUnread && 'opacity-0'
                )}
            />
            <CategoryTile category={category} />
            <div className="flex min-w-0 flex-1 flex-col gap-px">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                    {/* The basis keeps the time beside a title that wraps; under it only when the row is narrower. */}
                    <p
                        className={cn(
                            'text-body1 min-w-0 flex-[1_1_12rem]',
                            isUnread ? 'font-semibold' : 'font-medium'
                        )}
                    >
                        {title}
                    </p>
                    {time ? <div className="flex-none">{time}</div> : null}
                </div>
                <div className="text-meta text-foreground-secondary leading-[1.54] text-pretty">{body}</div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="text-primary-ink-muted text-caption font-medium">{t(category.labelId)}</span>
                    {footer}
                </div>
            </div>
        </li>
    );
}

interface FeedItemProps {
    entry: FeedEntry;
    onToggleRead: (entry: FeedEntry) => void;
    onArchive: (entry: FeedEntry) => void;
    areButtonsDisabled: boolean;
}

/** A notification in the feed, with its read toggle and its archive action. */
export function InboxFeedItem({ entry, onToggleRead, onArchive, areButtonsDisabled }: FeedItemProps) {
    const { item, category } = entry;
    const isUnread = !item.readAt;
    const draft = item.metaDataJson?.draft;
    const draftKind = draftNotificationKind(item);
    const draftTitle = draftKind
        ? getIntl().formatMessage({ id: `notification.draft-${draftKind}` }, draftNotificationValues(item, getIntl()))
        : undefined;

    return (
        <FeedRow
            category={draft && draftKind ? { ...category, ...draftTile(draft.type) } : category}
            isUnread={isUnread}
            title={draftTitle ?? item.title}
            time={<TimeSinceDate date={item.createdAt} />}
            body={<FormattedNotifcationText notification={item} />}
            footer={
                <>
                    {item.isImportant ? (
                        <StatusChip
                            type="warning"
                            label={<FormattedMessage id="notifications.important" values={{ count: 0 }} />}
                        />
                    ) : null}
                    <span className="flex-1" />
                    {/* One group, so the two actions wrap together under the category on a narrow panel. */}
                    <span className="flex items-center gap-3">
                        <Button
                            variant="link"
                            size="xs"
                            className={ROW_ACTION_CLASS}
                            onClick={() => onToggleRead(entry)}
                            disabled={areButtonsDisabled}
                        >
                            <FormattedMessage
                                id={isUnread ? 'notifications.mark-as-read' : 'notifications.mark-as-unread'}
                            />
                        </Button>
                        <Button
                            variant="link"
                            size="xs"
                            className={ROW_ACTION_CLASS}
                            onClick={() => onArchive(entry)}
                            disabled={areButtonsDisabled}
                        >
                            <FormattedMessage id="notifications.archive" />
                        </Button>
                    </span>
                </>
            }
        />
    );
}

/**
 * An open compliance task, stored or automatic. It has no date, no read state and no archive: it leaves the
 * Inbox when it is done. A stored task's title opens the task; an automatic one's opens the client's
 * Compliance tab, where the check it asks for is done.
 */
export function ComplianceTaskFeedItem({
    task,
    category,
    classifiers,
}: {
    task: AddressedTaskListItem;
    category: InboxCategory;
    classifiers: Record<string, ClassifierData>;
}) {
    const dispatch = useDispatch();
    const party = task.clientParties?.[0];
    const people = [
        { labelId: 'contacts.relationship-partners', list: party?.relationshipPartners },
        { labelId: 'contacts.relationship-managers', list: party?.relationshipManagers },
    ].filter(({ list }) => !!list?.length);
    const url = getTaskUrl(task);

    return (
        <FeedRow
            category={category}
            isUnread={false}
            title={
                url ? (
                    <LinkWithOrigin
                        to={url}
                        className="hover:text-link hover:underline"
                        onClick={() => dispatch(closeTemporaryDrawer())}
                    >
                        {task.name}
                    </LinkWithOrigin>
                ) : (
                    task.name
                )
            }
            body={
                <>
                    {party ? <PartyName party={party} isLink variant="readonly-other" /> : null}
                    {task.matters?.map((matter) => (
                        <p key={matter.id} className="flex items-center gap-1">
                            <MatterIndicators isConfidential={matter.isConfidential} />
                            {matter.name}
                        </p>
                    ))}
                    {people.map(({ labelId, list }) => (
                        <p key={labelId}>
                            {t(labelId)}:{' '}
                            {getIntl().formatList(
                                (list ?? []).map((person) => (
                                    <RecordChip
                                        key={person.id}
                                        entityType="EMPLOYEE"
                                        id={person.id}
                                        label={person.name}
                                        hitArea={false}
                                    />
                                )),
                                { type: 'unit' }
                            )}
                        </p>
                    ))}
                </>
            }
            footer={
                <StatusChip
                    type={getPriorityStatusChipType(task.taskPriorityTypeCode)}
                    label={getClassifierText(classifiers['TASK_PRIORITY_TYPE'], task.taskPriorityTypeCode)}
                />
            }
        />
    );
}
